import { h, Component, Host, Prop, Element, Watch } from '@stencil/core';

import { isNumber } from '../../../../services/utils';

@Component({
  tag: 'pk-progress-stat-item',
  styleUrl: './scss/pk-progress-stat-item.scss',
  shadow: true,
})
export class PkProgressStatItem {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================
  @Element() el!: HTMLPkProgressStatItemElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================
  // @State() private _src: string;

  // Public Property API
  // ========================

  /** Specify the animation time in seconds*/
  @Prop() animationTime?: number = 1;

  /** Value of the progress line tracking in a range of 0 - 100 */
  @Prop() progressPercentage: number;

  /** Background color of the progress line bar element */
  @Prop() progressColor?: string;

  /** Background color of the tracking line area element */
  @Prop() trackAreaColor?: string;

  /** Text value to display in the tooltip element */
  @Prop() tooltipText!: string;

  // Prop lifecycle events
  // =======================

  @Watch('tooltipText')
  handleTooltipTextProp() {
    if (this.tooltipText) return;
    console.error(`[pk-progress-stat-item]: "tooltip-text" attribute value is missing 😵!!`);
  }

  @Watch('progressPercentage')
  handleProgressPercentageProp() {
    if (isNumber(this.progressPercentage)) return;
    console.error(`[pk-progress-stat-item]: "progress-percentage" attribute value should be a number 🚫!!`);
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  // Called once just after the component fully loaded and the first render() occurs

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private handleAlignment = () => {
    if (Number(this.progressPercentage) < 5) return 'left';
    if (Number(this.progressPercentage) > 95) return 'right';
    return 'center';
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const align = this.handleAlignment();
    const animation = `progress-line ${this.animationTime}s ease`;
    const left = Number(this.progressPercentage > 100 ? 100 : this.progressPercentage) || 0;

    const style = {
      ...(this.animationTime && { '--progress-stat-animation-duration': `${this.animationTime}s` }),
    };

    return (
      <Host style={{ ...style }}>
        <div class="progress-wrapper" part="base">
          {this.tooltipText && (
            <div
              class={`relative px-[--progress-stat-spacing] py-0 tooltip-${align}`}
              pk-theme="dark"
              part="tooltip-wrapper"
            >
              <div
                pk-tooltip={this.tooltipText}
                pk-tooltip-align={align}
                style={{ left: `${left}%` }}
                part="tooltip-text"
              />
            </div>
          )}
          <pk-progress-indicator
            animationEffect={animation}
            percentages={this.progressPercentage}
            colors={this.progressColor}
            trackAreaColor={this.trackAreaColor}
            part="progress-indicator"
          />
        </div>
      </Host>
    );
  }
}
